import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "deviation-cards"
}
const _hoisted_2 = { class: "card-header" }
const _hoisted_3 = { class: "title-left" }
const _hoisted_4 = { class: "card-title" }
const _hoisted_5 = {
  key: 0,
  class: "card-value"
}
const _hoisted_6 = { class: "card-footer" }
const _hoisted_7 = {
  key: 0,
  class: "range-box"
}
const _hoisted_8 = { class: "range-bar" }
const _hoisted_9 = { class: "range-labels" }
const _hoisted_10 = { class: "range-min" }
const _hoisted_11 = { class: "range-max" }
const _hoisted_12 = {
  key: 1,
  class: "time-info"
}
const _hoisted_13 = { class: "unit-text" }
const _hoisted_14 = {
  key: 1,
  class: "deviation-charts"
}
const _hoisted_15 = { class: "chart-header" }
const _hoisted_16 = { class: "title-left" }
const _hoisted_17 = { class: "chart-title" }
const _hoisted_18 = {
  key: 0,
  class: "chart-value"
}
const _hoisted_19 = {
  key: 0,
  class: "chart-footer"
}
const _hoisted_20 = { class: "time-info" }
const _hoisted_21 = { class: "unit-text" }
const _hoisted_22 = {
  key: 1,
  class: "chart-content"
}
const _hoisted_23 = ["src"]

import { ref, watch, onUnmounted, nextTick } from 'vue'
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { toPng } from 'html-to-image'
import { saveTrainingItemScorePicture } from '@/service'
import { getDeviationColor } from '@/utils'

// 注册 ECharts 模块
interface DataPoint {
    time: number
    value: number
}

// 图表数据存储
interface ChartDataStore {
    name: string
    id: any
    unit: string
    min: number
    max: number
    color: string
    dataPoints: DataPoint[]
    isEvaluated: boolean
    pictureUrl?: string
}

const INITIAL_TIME_RANGE = 20

const MAX_POINT_SIZE = 75

const MAX_DRAW_INTERVAL = 20

// 设置图表容器引用

export default /*@__PURE__*/_defineComponent({
  __name: 'flyDeviationInfo',
  props: {
    isMouseHidden: {
        type: Boolean,
        default: false
    },
    isReady: {
        type: Boolean,
        default: false
    },
    flyingInfo: {
        type: [Array, Object],
        default: () => []
    },
    viewMode: {
        type: String,
        default: 'card'
    },
    // 是否使用静态图片模式（成绩结算页面使用后端返回的图片，飞行页面使用 echarts 绘制）
    useStaticImage: {
        type: Boolean,
        default: false
    }
},
  setup(__props, { expose: __expose }) {

echarts.use([LineChart, GridComponent, CanvasRenderer])

// 数据点类型
const props = __props

// 图表容器引用
const chartRefs = ref<Record<string, HTMLElement>>({})

// 图表卡片元素引用（用于导出图片）
const chartItemRefs = ref<Record<string, HTMLElement>>({})

// ECharts 实例存储
const chartInstances = ref<Record<string, echarts.ECharts>>({})

// 曲线数据存储
const chartDataStore = ref<Record<string, ChartDataStore>>({})

// 当前时间计数（秒）
const currentTimeCounter = ref(0)

// 初始显示时间范围（秒）
let DRAW_INTERVAL = 0
const setChartRef = (el: any, key: string) => {
    if (el) {
        chartRefs.value[key] = el
    }
}

// 设置图表卡片元素引用
const setChartItemRef = (el: any, key: string) => {
    if (el) {
        chartItemRefs.value[key] = el
    }
}

// 初始化单个图表
const initChart = (key: string) => {
    const container = chartRefs.value[key]
    if (!container) return

    // 销毁已存在的实例
    if (chartInstances.value[key]) {
        chartInstances.value[key].dispose()
    }

    // 创建新实例
    const chart = echarts.init(container)
    chartInstances.value[key] = chart

    // 获取数据
    const dataStore = chartDataStore.value[key]
    if (!dataStore) return

    // 计算Y轴范围
    const [yMin, yMax] = calculateYAxisRange(dataStore)

    // 计算X轴范围（动态增长）
    const maxX = Math.max(dataStore.dataPoints[dataStore.dataPoints.length - 1]?.time || INITIAL_TIME_RANGE, INITIAL_TIME_RANGE)

    // 配置项
    const option: echarts.EChartsCoreOption = {
        animation: false,
        grid: {
            left: 32,
            right: 8,
            top: 0,
            bottom: 0
        },
        xAxis: {
            type: 'value',
            min: 0,
            max: maxX,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false }
        },
        yAxis: {
            type: 'value',
            min: yMin,
            max: yMax,
            splitNumber: 3,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: {
                lineStyle: { color: 'rgba(255, 255, 255, 0.2)', width: 0.5 }
            },
            axisLabel: {
                fontFamily: 'HarmonyOS Sans SC',
                fontSize: 10,
                color: 'rgba(255, 255, 255, 0.6)',
                formatter: (value: number) => value.toFixed(1)
            }
        },
        series: [{
            type: 'line',
            data: dataStore.dataPoints.map(p => [p.time, p.value]),
            smooth: 0.6,
            symbol: 'none',
            lineStyle: {
                color: '#2174FF',
                width: 1,
                shadowColor: 'rgba(96, 91, 255, 0.17)',
                shadowBlur: 4,
                shadowOffsetY: 4
            },
            areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: '#2174FF' },
                    { offset: 0.39, color: 'rgba(33, 116, 255, 0.5)' },
                    { offset: 1, color: 'rgba(0, 0, 0, 0)' }
                ])
            }
        }]
    }
    chart.setOption(option)
}

// 计算Y轴范围（自适应）
const calculateYAxisRange = (dataStore: ChartDataStore): [number, number] => {
    let min = dataStore.min;
    let max = dataStore.max;
    if (dataStore.isEvaluated && dataStore.dataPoints.length > 0) {
        const values = dataStore.dataPoints.map(p => p.value)
        const actualMin = Math.min(...values)
        const actualMax = Math.max(...values)

        if (actualMin < min) {
            min = actualMin;
        }
        if (actualMax > dataStore.max) {
            max = actualMax;

        }
    }
    return [min - (max - min) / 4, max + 0.5];
}

// 初始化数据存储
const initDataStore = () => {
    const flyingData = (props.flyingInfo || {}) as any
    Object.keys(flyingData).forEach(key => {
        const item = flyingData[key]
        if (key === 'timeDeviation') {
            chartDataStore.value[key] = {
                name: item[0]?.name || '用时偏差',
                unit: item[0]?.unit || 's',
                min: 0,
                max: item[0]?.max || 60,
                color: getDeviationColor(key),
                dataPoints: [],
                isEvaluated: false,
                id: item[0]?.id,
                pictureUrl: item[0]?.pictureUrl
            }
        } else {
            chartDataStore.value[key] = {
                name: item.name || key,
                unit: item.unit || '',
                min: item.min || 0,
                max: item.max || 100,
                color: getDeviationColor(key),
                dataPoints: [],
                isEvaluated: false,
                id: item?.id,
                pictureUrl: item?.pictureUrl
            }
        }
    })
}

// 更新图表
const doUpdateChart = (key: string) => {
    const chart = chartInstances.value[key]
    const dataStore = chartDataStore.value[key]
    if (!chart || !dataStore) return
    let dataPoints = dataStore.dataPoints
    if (dataPoints.length === 0) return
    let showDataPoints = [];
    let interval = Math.ceil(dataPoints.length / MAX_POINT_SIZE);
    if (interval > 1) {
        for (let index = 0; index < dataPoints.length; index++) {
            if (index % interval == 0) {
                showDataPoints.push(dataPoints[index]);
            }
        }
        dataPoints = showDataPoints;
    }

    // 计算Y轴范围
    const [yMin, yMax] = calculateYAxisRange(dataStore)
    const maxX = Math.max(dataPoints[dataPoints.length - 1].time, INITIAL_TIME_RANGE)
    // 更新图表
    chart.setOption({
        xAxis: { min: 0, max: maxX },
        yAxis: { min: yMin, max: yMax },
        series: [{
            data: dataPoints.map(p => [p.time, p.value])
        }]
    })
}

// Base64 转 File（文件名用当前时间戳）
async function base64ToFile(blob: any): Promise<File> {
    const ext = blob.type.split('/')[1] || 'png'
    const timestamp = Date.now()
    return new File([blob], `${timestamp}.${ext}`, { type: blob.type })
}

// 带重试机制的获取图表 DataURL
async function getChartDataURLWithRetry(chart: any, maxRetries = 3, retryDelay = 50): Promise<string | null> {
    for (let i = 0; i < maxRetries; i++) {
        const dataUrl = chart.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: 'transparent'
        })
        if (dataUrl) {
            return dataUrl
        }
        if (i < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, retryDelay))
        }
    }
    return null
}

// 上传所有
const uploadAllChartImages = async (trainingId: any) => {
    // 静态图片模式不需要上传
    if (props.useStaticImage) return
    for (const [key, chart] of Object.entries(chartInstances.value)) {
        const dataStore = chartDataStore.value[key]

        if (!dataStore || !dataStore.id) continue
        const dataUrl = await getChartDataURLWithRetry(chart)
        if (!dataUrl) {
            continue
        }
        const blob = await fetch(dataUrl).then(r => r.blob())
        const file = await base64ToFile(blob)
        const formData = new FormData()
        formData.append('imageFile', file)
        formData.append('trainingId', trainingId)
        formData.append('itemId', dataStore.id)
        await saveTrainingItemScorePicture(formData, {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        })


    }
}

// 导出单个图表卡片为图片
const exportChartImage = async (key: string): Promise<string | null> => {
    const element = chartItemRefs.value[key]
    if (!element) return null

    try {
        const dataUrl = await toPng(element, {
            quality: 1.0,
            pixelRatio: 2,
            backgroundColor: 'rgba(21, 24, 29, 0.4)'
        })
        return dataUrl
    } catch (error) {
        console.error(`导出图表 ${key} 失败:`, error)
        return null
    }
}

// 重置所有曲线数据
const resetCharts = () => {
    // 重置时间计数
    currentTimeCounter.value = 0
    chartDataStore.value = {}
    // 销毁所有图表实例
    Object.keys(chartInstances.value).forEach(key => {
        if (chartInstances.value[key]) {
            chartInstances.value[key].dispose()
        }
    })
    chartInstances.value = {}

    // 清空数据存储（让 watch 重新初始化）
    chartDataStore.value = {}

    // 如果当前是曲线模式且有数据，重新初始化
    if (props.viewMode !== 'card' && Object.keys(props.flyingInfo || {}).length > 0) {
        initDataStore()
        Object.keys(chartDataStore.value).forEach(key => {
            if (key === 'timeDeviation') return
            if (chartRefs.value[key]) {
                initChart(key)
            }
        })
    }
}

// 获取曲线数据（用于导出到结算页）
const getChartData = () => {
    return {
        dataStore: JSON.parse(JSON.stringify(chartDataStore.value)),
        timeCounter: currentTimeCounter.value
    }
}

// 导入曲线数据（用于结算页显示飞行页的曲线）
const importChartData = async (data: { dataStore: Record<string, ChartDataStore>, timeCounter: number }) => {
    if (!data || !data.dataStore) return

    // 导入数据
    currentTimeCounter.value = data.timeCounter || 0
    chartDataStore.value = JSON.parse(JSON.stringify(data.dataStore))

    // 如果当前是曲线模式，等待 DOM 更新后初始化图表
    if (props.viewMode !== 'card') {
        await nextTick()
        // 等待一帧确保 ref 回调已执行，chartRefs 已填充
        await new Promise<void>(resolve => {
            requestAnimationFrame(() => {
                resolve()
            })
        })
        // 同步初始化所有图表（此时 chartRefs 一定已填充）
        Object.keys(chartDataStore.value).forEach(key => {
            if (key === 'timeDeviation') return
            if (chartRefs.value[key]) {
                initChart(key)
            }
        })
    }
}

// 更新曲线数据（供父组件主动调用）
const updateCurveData = (shouldUpdateChart = true) => {

    // 首次初始化
    if (Object.keys(chartDataStore.value).length === 0) {
        initDataStore()
        return
    }

    // 增加时间计数
    currentTimeCounter.value++

    DRAW_INTERVAL++;
    let needDrawCurve = DRAW_INTERVAL >= MAX_DRAW_INTERVAL;
    // 遍历每个数据项，收集数据点
    const flyingData = (props.flyingInfo || {}) as any
    Object.keys(flyingData).forEach(key => {
        if (key === 'timeDeviation') return

        const item = flyingData[key]
        const dataStore = chartDataStore.value[key]
        if (!dataStore || item.realTimeValue === undefined) return

        if (shouldUpdateChart) {
            // 标记为已开始评判
            dataStore.isEvaluated = true

            // 添加数据点（使用 realTimeValue）
            dataStore.dataPoints.push({
                time: currentTimeCounter.value,
                value: item.realTimeValue
            })

            // 更新图表
            if (shouldUpdateChart && needDrawCurve && props.viewMode !== 'card') {
                if (!chartInstances.value[key] && chartRefs.value[key]) {
                    initChart(key)
                } else if (chartInstances.value[key] && needDrawCurve) {
                    doUpdateChart(key)
                    DRAW_INTERVAL = 0;
                }
            }
        }

    })
}

// 暴露方法给父组件
__expose({
    uploadAllChartImages,
    exportChartImage,
    chartDataStore,
    resetCharts,
    getChartData,
    importChartData,
    updateCurveData
})

// 监听飞行信息变化 - 补偿初始化：当 chartDataStore 为空且有数据时自动初始化
watch(() => props.flyingInfo, () => {
    if (Object.keys(chartDataStore.value).length === 0 && Object.keys(props.flyingInfo || {}).length > 0) {
        initDataStore()
    }
}, { immediate: true, deep: true })

// 监听视图模式变化
watch(() => props.viewMode, async (newMode) => {
    // 静态图片模式不需要初始化 echarts
    if (props.useStaticImage) return

    if (newMode !== 'card') {
        // 如果 chartDataStore 为空但有 flyingInfo 数据，先初始化数据存储
        if (Object.keys(chartDataStore.value).length === 0 && Object.keys(props.flyingInfo || {}).length > 0) {
            initDataStore()
        }

        // 等待 DOM 更新完成
        await nextTick()
        // 再等待一帧确保 ref 回调已执行
        requestAnimationFrame(() => {
            // 初始化所有图表
            Object.keys(chartDataStore.value).forEach(key => {
                if (key === 'timeDeviation') return
                if (chartRefs.value[key]) {
                    initChart(key)
                }
            })
        })
    }
}, { immediate: true })

// 组件卸载时清理
onUnmounted(() => {
    // 清理图表实例
    Object.values(chartInstances.value).forEach(chart => {
        chart.dispose()
    })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "deviation-info-container",
    style: _normalizeStyle({ cursor: props.isMouseHidden ? 'none !important' : 'default !important' })
  }, [
    (props.viewMode === 'card')
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.flyingInfo, (item, key) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `fly${key}`,
              class: _normalizeClass(key == 'timeDeviation' ? 'time-deviation-item deviation-card' : 'deviation-card')
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("span", _hoisted_3, [
                  _createElementVNode("span", {
                    class: "color-dot",
                    style: _normalizeStyle({ background: _unref(getDeviationColor)(key as string) })
                  }, null, 4),
                  _createElementVNode("span", _hoisted_4, _toDisplayString(key != 'timeDeviation' ? item.name : item[0]?.name) + _toDisplayString(key !=
                            'timeDeviation' ? `(${item.unit})` : ''), 1)
                ]),
                (key != 'timeDeviation')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
                      _createElementVNode("span", {
                        style: _normalizeStyle({ color: item.lowerValue < item.min ? '#E70102' : '#FFFFFF' })
                      }, _toDisplayString(item.lowerValue), 5),
                      _cache[0] || (_cache[0] = _createElementVNode("span", { class: "value-separator" }, "~", -1)),
                      _createElementVNode("span", {
                        style: _normalizeStyle({ color: item.higherValue > item.max ? '#E70102' : '#FFFFFF' })
                      }, _toDisplayString(item.higherValue), 5)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_6, [
                (key != 'timeDeviation')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("div", _hoisted_8, [
                        _createElementVNode("div", {
                          class: "range-fill",
                          style: _normalizeStyle({ width: `${item.progress}%`, backgroundColor: item.color, left: `${item.left}%` })
                        }, null, 4)
                      ]),
                      _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("span", _hoisted_10, _toDisplayString(item.min), 1),
                        _createElementVNode("span", _hoisted_11, _toDisplayString(item.max), 1)
                      ])
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item, (childItem, childIndex) => {
                        return (_openBlock(), _createElementBlock("p", {
                          key: childIndex,
                          class: "time-info-text"
                        }, [
                          _createElementVNode("span", {
                            class: _normalizeClass(childItem?.value > childItem?.max ? 'time-out' : 'none-timeout')
                          }, _toDisplayString(childItem?.value), 3),
                          _createElementVNode("span", _hoisted_13, "/" + _toDisplayString(childItem?.max) + _toDisplayString(childItem.unit), 1)
                        ]))
                      }), 128))
                    ]))
              ])
            ], 2))
          }), 128))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_14, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.flyingInfo, (item, key) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `chart${key}`,
              class: _normalizeClass(key == 'timeDeviation' ? 'time-deviation-item chart-item' : 'chart-item'),
              ref_for: true,
              ref: el => setChartItemRef(el, key as string)
            }, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("span", _hoisted_16, [
                  _createElementVNode("span", {
                    class: "color-dot",
                    style: _normalizeStyle({ background: _unref(getDeviationColor)(key as string) })
                  }, null, 4),
                  _createElementVNode("span", _hoisted_17, _toDisplayString(item.name || item[0]?.name) + _toDisplayString(key != 'timeDeviation' ?
                            `(${item.unit})` : ''), 1)
                ]),
                (key != 'timeDeviation')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_18, [
                      _createElementVNode("span", {
                        style: _normalizeStyle({ color: item.lowerValue < item.min ? '#E70102' : '#FFFFFF' })
                      }, _toDisplayString(item.lowerValue), 5),
                      _cache[1] || (_cache[1] = _createElementVNode("span", { class: "value-separator" }, "~", -1)),
                      _createElementVNode("span", {
                        style: _normalizeStyle({ color: item.higherValue > item.max ? '#E70102' : '#FFFFFF' })
                      }, _toDisplayString(item.higherValue), 5)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              (key == 'timeDeviation')
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createElementVNode("div", _hoisted_20, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item, (childItem, childIndex) => {
                        return (_openBlock(), _createElementBlock("p", {
                          key: childIndex,
                          class: "time-info-text font-DIN"
                        }, [
                          _createElementVNode("span", {
                            class: _normalizeClass(childItem?.value > childItem?.max ? 'time-out' : 'none-timeout')
                          }, _toDisplayString(childItem?.value), 3),
                          _createElementVNode("span", _hoisted_21, "/" + _toDisplayString(childItem?.max) + _toDisplayString(childItem.unit), 1)
                        ]))
                      }), 128))
                    ])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_22, [
                    (__props.useStaticImage)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: item.pictureUrl,
                          class: "chart-image",
                          alt: ""
                        }, null, 8, _hoisted_23))
                      : (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          class: "chart-canvas",
                          ref_for: true,
                          ref: el => setChartRef(el, key as string)
                        }, null, 512))
                  ]))
            ], 2))
          }), 128))
        ]))
  ], 4))
}
}

})